import type { ServerKeyPublic } from 'types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';

type CreatedServerKey = { key: ServerKeyPublic; secret: string };

/**
 * A website's server keys, for the SDK and /sdk/*. The list never holds a secret: create is
 * the one response that carries it, and the caller shows it once. Both writes throw the API's
 * `{ status, message }` (409 for an active key with the same name), so the dialog can say why.
 *
 * `enabled` keeps the list unfetched until the dialog opens: the websites list renders one
 * dialog per row, and a closed dialog has no reason to ask.
 */
export const useServerKeys = (websiteId: number, enabled = true) => {
  const queryClient = useQueryClient();
  const apiClient = useClient();
  const queryKey = ['SERVER_KEYS', websiteId];

  const {
    data: keys = [],
    isLoading,
    isError,
    refetch,
  } = useQuery<ServerKeyPublic[]>({
    queryKey,
    enabled: enabled && !!websiteId,
    queryFn: () => apiClient.get<ServerKeyPublic[]>(`/server-keys/${websiteId}`),
  });

  const activeKeys = keys.filter((key) => key.revokedAt === null);

  /** Resolves to the secret, which is never available again. */
  const create = async (name: string): Promise<string> => {
    const created = await apiClient.post<CreatedServerKey>(`/server-keys/${websiteId}`, { name: name.trim() });
    await queryClient.invalidateQueries({ queryKey });
    return created.secret;
  };

  /** Refreshes the list even when the revoke fails: a 404 usually means someone else revoked it first. */
  const revoke = async (keyId: number) => {
    try {
      await apiClient.delete(`/server-keys/${websiteId}/${keyId}`);
    } finally {
      await queryClient.invalidateQueries({ queryKey });
    }
  };

  const retry = async () => {
    await refetch();
  };

  return { keys, activeKeys, isLoading, isError, retry, create, revoke };
};
